'use client';

import { motion, AnimatePresence } from 'motion/react';
import {
    AlertTriangle,
    Info,
    ArrowRight,
    Building2,
    Clock,
    BarChart3,
    Lightbulb,
    ExternalLink,
    AlertCircle,
    HelpCircle,
    Flame,
    X
} from 'lucide-react';
import { useState, type ReactNode } from 'react';

import { Button } from '@/core/ui/button';
import { cn } from '@/core/utils/utils';

import type {
    ParsedBlock,
    InlineToken,
    SemanticHint,
    CollectionData
} from '../lib/parser';

import { CTACard } from './CTACard';

// ============================================================================
// Animation Config
// ============================================================================

const blockVariants = {
    hidden: { opacity: 0, y: 8 },
    visible: { opacity: 1, y: 0 },
};

const itemVariants = {
    hidden: { opacity: 0, x: -4 },
    visible: { opacity: 1, x: 0 },
};

// ============================================================================
// Inline Renderer (with nesting support)
// ============================================================================

function renderInline(tokens: InlineToken[]): ReactNode {
    return tokens.map((token, i) => {
        switch (token.type) {
            case 'bold':
                return (
                    <strong key={i} className="font-semibold text-foreground">
                        {token.children ? renderInline(token.children) : token.content}
                    </strong>
                );
            case 'italic':
                return <em key={i} className="italic">{token.content}</em>;
            case 'code':
                return (
                    <code key={i} className="px-1.5 py-0.5 bg-muted-foreground/10 rounded text-xs font-mono text-primary/90">
                        {token.content}
                    </code>
                );
            case 'link':
                return (
                    <a
                        key={i}
                        href={token.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary hover:text-primary/80 underline underline-offset-2 inline-flex items-center gap-0.5"
                    >
                        {token.content}
                        <ExternalLink className="w-3 h-3" />
                    </a>
                );
            case 'emoji':
                return <span key={i} className="mr-1">{token.content}</span>;
            default:
                return <span key={i}>{token.content}</span>;
        }
    });
}

// ============================================================================
// Block Components
// ============================================================================

function ParsedParagraph({ block }: { block: ParsedBlock }) {
    return (
        <motion.p
            variants={blockVariants}
            initial="hidden"
            animate="visible"
            transition={{ duration: 0.2 }}
            className="text-sm leading-relaxed text-muted-foreground mb-3 last:mb-0"
        >
            {block.inline ? renderInline(block.inline) : block.content}
        </motion.p>
    );
}

function ParsedHeading({ block }: { block: ParsedBlock }) {
    const level = block.level || 3;
    const sizes: Record<number, string> = {
        1: 'text-lg font-bold',
        2: 'text-base font-semibold',
        3: 'text-sm font-semibold',
        4: 'text-sm font-medium',
        5: 'text-xs font-medium uppercase tracking-wider',
        6: 'text-xs font-medium uppercase tracking-wider text-muted-foreground',
    };

    return (
        <motion.div
            variants={blockVariants}
            initial="hidden"
            animate="visible"
            transition={{ duration: 0.2 }}
            className={cn(
                sizes[level],
                'text-foreground mb-2 mt-4 first:mt-0',
                level <= 2 && 'bg-gradient-to-r from-primary/10 to-transparent -mx-2 px-2 py-1 rounded'
            )}
        >
            {block.inline ? renderInline(block.inline) : block.content}
        </motion.div>
    );
}

function ParsedTable({ block }: { block: ParsedBlock }) {
    const rows = block.rows || [];
    if (rows.length === 0) return null;

    const hint = block.hint;
    const _isMetrics = hint === 'metrics';
    const _isComparison = hint === 'comparison';
    const _isTimeline = hint === 'timeline';

    const hintIcons: Partial<Record<SemanticHint, ReactNode>> = {
        'metrics': <BarChart3 className="w-3.5 h-3.5 text-primary" />,
        'comparison': <ArrowRight className="w-3.5 h-3.5 text-primary" />,
        'timeline': <Clock className="w-3.5 h-3.5 text-blue-500" />,
    };

    return (
        <motion.div
            variants={blockVariants}
            initial="hidden"
            animate="visible"
            transition={{ duration: 0.25 }}
            className={cn(
                'my-3 rounded-lg border border-border/50 overflow-hidden',
                'bg-gradient-to-br from-muted/30 to-muted/10 backdrop-blur-sm'
            )}
        >
            {hint && (
                <div className="px-3 py-1.5 border-b border-border/30 flex items-center gap-2">
                    {hintIcons[hint]}
                    <span className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
                        {hint.replace('-', ' ')}
                    </span>
                </div>
            )}
            <table className="w-full text-xs">
                <tbody>
                    {rows.map((row, rowIdx) => (
                        <tr
                            key={rowIdx}
                            className={cn(
                                row.isHeader
                                    ? 'bg-muted/40 font-medium text-foreground'
                                    : 'hover:bg-muted/20 transition-colors',
                                rowIdx > 0 && 'border-t border-border/20'
                            )}
                        >
                            {row.cells.map((cell, cellIdx) => (
                                <td
                                    key={cellIdx}
                                    className={cn(
                                        'px-3 py-2',
                                        row.isHeader && 'text-muted-foreground',
                                        cellIdx === 0 && 'font-medium'
                                    )}
                                >
                                    {cell}
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        </motion.div>
    );
}

function ParsedList({ block }: { block: ParsedBlock }) {
    const items = block.items || [];
    if (items.length === 0) return null;

    const hint = block.hint;
    const isOrdered = block.ordered;
    const isSteps = hint === 'steps' || isOrdered;
    const _isRecommendation = hint === 'recommendation';
    const isWarning = hint === 'warning';

    const hintConfig: Record<string, { icon: ReactNode; color: string }> = {
        'steps': { icon: <Clock className="w-3.5 h-3.5 text-primary" />, color: 'text-primary' },
        'recommendation': { icon: <Lightbulb className="w-3.5 h-3.5 text-amber-500" />, color: 'text-amber-500' },
        'warning': { icon: <AlertTriangle className="w-3.5 h-3.5 text-red-500" />, color: 'text-red-500' },
    };

    const config = hint ? hintConfig[hint] : null;

    return (
        <motion.div
            variants={blockVariants}
            initial="hidden"
            animate="visible"
            transition={{ duration: 0.2 }}
            className="my-3"
        >
            {hint && config && (
                <div className="flex items-center gap-2 mb-2">
                    {config.icon}
                    <span className={cn('text-[10px] font-semibold uppercase tracking-widest', config.color)}>
                        {hint}
                    </span>
                </div>
            )}
            <ul className="space-y-1.5">
                {items.map((item, i) => (
                    <motion.li
                        key={i}
                        className="flex items-start gap-2 text-sm text-muted-foreground"
                        variants={itemVariants}
                        initial="hidden"
                        animate="visible"
                        transition={{ duration: 0.15, delay: i * 0.03 }}
                    >
                        {isOrdered ? (
                            <span className="mt-0.5 min-w-[1.25rem] h-5 flex items-center justify-center rounded bg-primary/10 text-primary text-xs font-medium">
                                {i + 1}
                            </span>
                        ) : (
                            <span className={cn(
                                'mt-1.5 w-1.5 h-1.5 rounded-full shrink-0',
                                isSteps ? 'bg-primary' : isWarning ? 'bg-red-500' : 'bg-muted-foreground/50'
                            )} />
                        )}
                        <span className="flex-1">{item.inline ? renderInline(item.inline) : item.content}</span>
                    </motion.li>
                ))}
            </ul>
        </motion.div>
    );
}

function ParsedCode({ block }: { block: ParsedBlock }) {
    return (
        <motion.div
            variants={blockVariants}
            initial="hidden"
            animate="visible"
            transition={{ duration: 0.2 }}
            className="my-3"
        >
            {block.language && (
                <div className="px-3 py-1 bg-muted/60 border border-border/50 border-b-0 rounded-t-lg">
                    <span className="text-[10px] font-mono text-muted-foreground uppercase">
                        {block.language}
                    </span>
                </div>
            )}
            <pre className={cn(
                'p-3 bg-muted/40 border border-border/50 overflow-x-auto text-xs font-mono',
                block.language ? 'rounded-b-lg' : 'rounded-lg'
            )}>
                <code className="text-foreground/90">{block.content}</code>
            </pre>
        </motion.div>
    );
}

function ParsedBlockquote({ block }: { block: ParsedBlock }) {
    return (
        <motion.blockquote
            variants={blockVariants}
            initial="hidden"
            animate="visible"
            transition={{ duration: 0.2 }}
            className="my-3 pl-4 border-l-2 border-primary/50 text-sm italic text-muted-foreground"
        >
            {block.inline ? renderInline(block.inline) : block.content}
        </motion.blockquote>
    );
}

function ParsedCallout({ block }: { block: ParsedBlock }) {
    const calloutType = block.calloutType || 'note';

    const calloutStyles: Record<string, { icon: ReactNode; bg: string; border: string; text: string }> = {
        note: {
            icon: <Info className="w-4 h-4" />,
            bg: 'from-blue-500/10 to-blue-500/5',
            border: 'border-blue-500/30',
            text: 'text-blue-600 dark:text-blue-400'
        },
        tip: {
            icon: <Lightbulb className="w-4 h-4" />,
            bg: 'from-emerald-500/10 to-emerald-500/5',
            border: 'border-emerald-500/30',
            text: 'text-emerald-600 dark:text-emerald-400'
        },
        important: {
            icon: <AlertCircle className="w-4 h-4" />,
            bg: 'from-purple-500/10 to-purple-500/5',
            border: 'border-purple-500/30',
            text: 'text-purple-600 dark:text-purple-400'
        },
        warning: {
            icon: <AlertTriangle className="w-4 h-4" />,
            bg: 'from-amber-500/10 to-amber-500/5',
            border: 'border-amber-500/30',
            text: 'text-amber-600 dark:text-amber-400'
        },
        caution: {
            icon: <Flame className="w-4 h-4" />,
            bg: 'from-red-500/10 to-red-500/5',
            border: 'border-red-500/30',
            text: 'text-red-600 dark:text-red-400'
        },
    };

    const style = calloutStyles[calloutType] || calloutStyles.note;

    return (
        <motion.div
            variants={blockVariants}
            initial="hidden"
            animate="visible"
            transition={{ duration: 0.2 }}
            className={cn(
                'my-3 p-3 rounded-lg border bg-gradient-to-br',
                style.bg,
                style.border
            )}
        >
            <div className={cn('flex items-center gap-2 mb-1 font-medium text-sm', style.text)}>
                {style.icon}
                <span className="uppercase text-xs tracking-wider">{calloutType}</span>
            </div>
            <div className="text-sm text-foreground/80">
                {block.inline ? renderInline(block.inline) : block.content}
            </div>
        </motion.div>
    );
}

function ParsedHr() {
    return (
        <motion.hr
            variants={blockVariants}
            initial="hidden"
            animate="visible"
            className="my-4 border-border/30"
        />
    );
}

// ============================================================================
// Collection Card Component
// ============================================================================

interface CollectionCardProps {
    collection: CollectionData;
    onSubmit: (answer: string | string[] | number) => void;
    onSkip: () => void;
}

function CollectionCard({ collection, onSubmit, onSkip }: CollectionCardProps) {
    const [selectedOptions, setSelectedOptions] = useState<string[]>([]);
    const [inputValue, setInputValue] = useState('');
    const [scaleValue, _setScaleValue] = useState<number | null>(null);

    const handleTagClick = (option: string) => {
        if (collection.collectionType === 'tags') {
            setSelectedOptions(prev =>
                prev.includes(option)
                    ? prev.filter(o => o !== option)
                    : [...prev, option]
            );
        } else {
            onSubmit(option);
        }
    };

    const handleTagsSubmit = () => {
        if (selectedOptions.length > 0) {
            onSubmit(selectedOptions);
        }
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 10, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.98 }}
            transition={{ duration: 0.25 }}
            className="my-3 p-4 rounded-xl border border-primary/20 bg-gradient-to-br from-primary/5 to-primary/10"
        >
            <div className="flex items-start justify-between gap-2 mb-3">
                <div className="flex items-center gap-2">
                    <HelpCircle className="w-4 h-4 text-primary" />
                    <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                        Quick Question
                    </span>
                </div>
                <button
                    onClick={onSkip}
                    className="text-muted-foreground hover:text-foreground transition-colors"
                >
                    <X className="w-4 h-4" />
                </button>
            </div>

            <p className="text-sm font-medium text-foreground mb-3">{collection.label}</p>

            {/* Choice / Tags Options */}
            {(collection.collectionType === 'choice' || collection.collectionType === 'tags') && collection.options && (
                <div className="flex flex-wrap gap-2">
                    {collection.options.map((option, i) => (
                        <button
                            key={i}
                            onClick={() => handleTagClick(option)}
                            className={cn(
                                'px-3 py-1.5 rounded-full text-xs font-medium transition-all',
                                selectedOptions.includes(option)
                                    ? 'bg-primary text-primary-foreground'
                                    : 'bg-muted/50 text-muted-foreground hover:bg-muted hover:text-foreground'
                            )}
                        >
                            {option}
                        </button>
                    ))}
                </div>
            )}

            {/* Tags submit button */}
            {collection.collectionType === 'tags' && selectedOptions.length > 0 && (
                <Button
                    size="sm"
                    className="mt-3"
                    onClick={handleTagsSubmit}
                >
                    Continue
                </Button>
            )}

            {/* Scale */}
            {collection.collectionType === 'scale' && (
                <div className="flex gap-2">
                    {[1, 2, 3, 4, 5].map((n) => (
                        <button
                            key={n}
                            onClick={() => onSubmit(n)}
                            className={cn(
                                'w-10 h-10 rounded-lg text-sm font-medium transition-all',
                                scaleValue === n
                                    ? 'bg-primary text-primary-foreground'
                                    : 'bg-muted/50 text-muted-foreground hover:bg-muted hover:text-foreground'
                            )}
                        >
                            {n}
                        </button>
                    ))}
                </div>
            )}

            {/* Input */}
            {collection.collectionType === 'input' && (
                <div className="flex gap-2">
                    <input
                        type="text"
                        value={inputValue}
                        onChange={(e) => setInputValue(e.target.value)}
                        placeholder={collection.placeholder || 'Type here...'}
                        className="flex-1 px-3 py-2 rounded-lg bg-muted/30 border border-border/50 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20"
                    />
                    <Button
                        size="sm"
                        onClick={() => inputValue && onSubmit(inputValue)}
                        disabled={!inputValue}
                    >
                        Submit
                    </Button>
                </div>
            )}
        </motion.div>
    );
}

// ============================================================================
// Fenced Block Components (Semantic Cards)
// ============================================================================

function FencedCard({ block }: { block: ParsedBlock }) {
    const hint = block.hint;

    const icons: Partial<Record<SemanticHint, ReactNode>> = {
        'property-card': <Building2 className="w-4 h-4" />,
        'timeline': <Clock className="w-4 h-4" />,
        'assessment': <BarChart3 className="w-4 h-4" />,
        'recommendation': <Lightbulb className="w-4 h-4" />,
        'warning': <AlertTriangle className="w-4 h-4 text-amber-500" />,
        'info': <Info className="w-4 h-4 text-blue-500" />,
    };

    const colors: Partial<Record<SemanticHint, string>> = {
        'property-card': 'from-emerald-500/10 to-emerald-500/5 border-emerald-500/20',
        'timeline': 'from-blue-500/10 to-blue-500/5 border-blue-500/20',
        'assessment': 'from-primary/10 to-primary/5 border-primary/20',
        'recommendation': 'from-amber-500/10 to-amber-500/5 border-amber-500/20',
        'warning': 'from-amber-500/10 to-amber-500/5 border-amber-500/20',
        'info': 'from-blue-500/10 to-blue-500/5 border-blue-500/20',
    };

    const colorClass = (hint && colors[hint]) || 'from-muted/30 to-muted/10 border-border/50';
    const icon = hint && icons[hint];

    return (
        <motion.div
            variants={blockVariants}
            initial="hidden"
            animate="visible"
            transition={{ duration: 0.25 }}
            className={cn(
                'my-3 rounded-xl border p-4 bg-gradient-to-br',
                colorClass
            )}
        >
            {hint && (
                <div className="flex items-center gap-2 mb-3 pb-2 border-b border-current/10">
                    {icon}
                    <span className="text-xs font-semibold uppercase tracking-widest text-foreground/80">
                        {hint.replace('-', ' ')}
                    </span>
                </div>
            )}
            <div className="space-y-2">
                {block.items?.map((item, i) => (
                    <BlockRenderer key={i} block={item} />
                ))}
            </div>
        </motion.div>
    );
}

// ============================================================================
// Main Block Renderer
// ============================================================================

interface BlockRendererProps {
    block: ParsedBlock;
    onCollectionSubmit?: (answer: string | string[] | number) => void;
    onCollectionSkip?: () => void;
    onCTAContactSubmit?: (contactType: string, contactValue: string) => void;
    onCTAClick?: (ctaType: string, url: string) => void;
}

export function BlockRenderer({ block, onCollectionSubmit, onCollectionSkip, onCTAContactSubmit, onCTAClick }: BlockRendererProps) {
    switch (block.type) {
        case 'paragraph':
            return <ParsedParagraph block={block} />;
        case 'heading':
            return <ParsedHeading block={block} />;
        case 'table':
            return <ParsedTable block={block} />;
        case 'list':
            return <ParsedList block={block} />;
        case 'code':
            return <ParsedCode block={block} />;
        case 'blockquote':
            return <ParsedBlockquote block={block} />;
        case 'callout':
            return <ParsedCallout block={block} />;
        case 'hr':
            return <ParsedHr />;
        case 'fenced':
            return <FencedCard block={block} />;
        case 'collection':
            if (block.collection && onCollectionSubmit && onCollectionSkip) {
                return (
                    <CollectionCard
                        collection={block.collection}
                        onSubmit={onCollectionSubmit}
                        onSkip={onCollectionSkip}
                    />
                );
            }
            return null;
        case 'cta':
            if (block.cta) {
                return (
                    <CTACard
                        cta={block.cta}
                        onContactSubmit={onCTAContactSubmit}
                        onCTAClick={onCTAClick}
                    />
                );
            }
            return null;
        default:
            return <ParsedParagraph block={block} />;
    }
}

// ============================================================================
// Full Content Renderer
// ============================================================================

interface ParsedContentProps {
    blocks: ParsedBlock[];
    onCollectionSubmit?: (answer: string | string[] | number) => void;
    onCollectionSkip?: () => void;
    onCTAContactSubmit?: (contactType: string, contactValue: string) => void;
    onCTAClick?: (ctaType: string, url: string) => void;
}

export function ParsedContent({
    blocks,
    onCollectionSubmit,
    onCollectionSkip,
    onCTAContactSubmit,
    onCTAClick
}: ParsedContentProps) {
    const [dismissedCollections, setDismissedCollections] = useState<Set<number>>(new Set());

    const handleSkip = (index: number) => {
        setDismissedCollections(prev => new Set([...prev, index]));
        onCollectionSkip?.();
    };

    const handleSubmit = (index: number, answer: string | string[] | number) => {
        // Dismiss the collection card after submission
        setDismissedCollections(prev => new Set([...prev, index]));
        onCollectionSubmit?.(answer);
    };

    return (
        <div className="parsed-content">
            <AnimatePresence mode="popLayout">
                {blocks.map((block, i) => {
                    // Skip dismissed collection cards
                    if (block.type === 'collection' && dismissedCollections.has(i)) {
                        return null;
                    }

                    return (
                        <BlockRenderer
                            key={i}
                            block={block}
                            onCollectionSubmit={(answer) => handleSubmit(i, answer)}
                            onCollectionSkip={() => handleSkip(i)}
                            onCTAContactSubmit={onCTAContactSubmit}
                            onCTAClick={onCTAClick}
                        />
                    );
                })}
            </AnimatePresence>
        </div>
    );
}
